import { useMemo, useState } from "react";
import { MessageSquarePlus, Search, X, MessageSquare, Trash2, Pencil } from "lucide-react";
import { ConversationResponseDTO } from "../../shared/constants";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { motion, AnimatePresence } from "motion/react";

interface ConversationSidebarProps {
  conversations: ConversationResponseDTO[];
  activeConversationId: number | null;
  onSelectConversation: (conversation: ConversationResponseDTO) => void;
  onStartNewConversation: () => void;
  onDeleteConversation: (conversationId: number) => void;
  onRenameConversation: (conversationId: number, newTitle: string) => void;
  onClose?: () => void;
  isMobile?: boolean;
  isFullPage?: boolean;
}

function formatTimestamp(value: string, t: (key: string) => string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return t('justNow');
  if (minutes < 60) return `${minutes} ${t('minAgo')}`;
  if (hours < 24) return `${hours} ${t('hourAgo')}`;
  if (days === 1) return t('yesterday');
  if (days < 7) return `${days} ${t('daysAgo')}`;

  return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

export function ConversationSidebar({
  conversations,
  activeConversationId,
  onSelectConversation,
  onStartNewConversation,
  onDeleteConversation,
  onRenameConversation,
  onClose,
  isMobile = false,
  isFullPage = false,
}: ConversationSidebarProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [deletingConversation, setDeletingConversation] = useState<ConversationResponseDTO | null>(null);

  const safeConversations = Array.isArray(conversations) ? conversations : [];

  const filteredConversations = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return safeConversations;
    return safeConversations.filter((conv) =>
      (conv.title || '').toLowerCase().includes(normalizedQuery),
    );
  }, [safeConversations, query]);

  const handleDeleteClick = (e: React.MouseEvent, conv: ConversationResponseDTO) => {
    e.stopPropagation();
    setDeletingConversation(conv);
  };

  const handleRenameClick = (e: React.MouseEvent, conv: ConversationResponseDTO) => {
    e.stopPropagation();
    if (editingId === conv.id) {
      handleEditSave();
      return;
    }
    setEditingId(conv.id);
    setEditTitle(conv.title || '');
  };

  const handleEditSave = () => {
    if (editingId === null) return;
    const trimmed = editTitle.trim();
    if (trimmed) {
      onRenameConversation(editingId, trimmed);
    }
    setEditingId(null);
    setEditTitle("");
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const confirmDelete = () => {
    if (deletingConversation) {
      onDeleteConversation(deletingConversation.id);
      setDeletingConversation(null);
    }
  };

  const cancelDelete = () => {
    setDeletingConversation(null);
  };

  return (
    <aside
      className={`flex h-full flex-col ${
        isFullPage
          ? "bg-transparent"
          : "bg-white/10 dark:bg-white/5 backdrop-blur-md border-r border-white/10"
      }`}
    >
      <div className="shrink-0 px-4 pt-5 pb-3 border-b border-white/10">
        <div className="flex items-center justify-center mb-3">
          <h2 className="text-lg font-semibold text-center">{t('dialogues')}</h2>
          {isMobile && onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-white/10 transition-colors"
              aria-label={t('close')}
            >
              <X size={18} />
            </button>
          )}
        </div>

        <button
          onClick={onStartNewConversation}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-white/10 dark:bg-white/10 backdrop-blur-sm border border-white/10 px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-white/20 dark:hover:bg-white/20 mb-3"
        >
          <MessageSquarePlus size={16} />
          {t('newConversation')}
        </button>

        <label className="relative block">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('searchConversation')}
            className="w-full rounded-xl bg-white/10 dark:bg-white/10 border border-white/10 py-2.5 pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-accent transition-colors"
          />
        </label>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
        {filteredConversations.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center gap-3 py-12 text-center text-muted-foreground"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
              <MessageSquare size={20} />
            </div>
            <p className="text-sm font-medium text-foreground">
              {safeConversations.length === 0 ? t('noConversations') : t('noSearchResults')}
            </p>
            <p className="text-xs">
              {safeConversations.length === 0 ? t('noConversationsHint') : t('noSearchResultsHint')}
            </p>
          </motion.div>
        ) : (
          <motion.div layout className="space-y-0.5">
            <AnimatePresence mode="popLayout">
              {filteredConversations.map((conv) => {
                const isActive = conv.id === activeConversationId;
                const isEditing = editingId === conv.id;

                return (
                  <motion.div
  key={conv.id}
  layout
  initial={{ opacity: 0, y: 8 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ opacity: 0, scale: 0.95 }}
  transition={{ duration: 0.15 }}
  onClick={() => onSelectConversation(conv)}
  whileHover={{ scale: 1.01 }}
  whileTap={{ scale: 0.98 }}
  className={`w-full flex flex-col gap-1 rounded-xl px-3 py-2.5 text-left transition-all duration-200 mb-0.5 cursor-pointer ${
    isActive
      ? 'bg-violet-500/30 border border-violet-400/40 shadow-lg shadow-violet-500/10'
      : 'hover:bg-white/10 border border-transparent'
  }`}
>
  <div className="flex items-start justify-between gap-2">
    {isEditing ? (
      <input
        autoFocus
        value={editTitle}
        onChange={(e) => setEditTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleEditSave();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            handleEditCancel();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`flex-1 min-w-0 rounded-md px-2 py-1 text-sm outline-none border ${
          isActive
            ? 'bg-violet-50/10 border-violet-300/30 text-violet-50 placeholder:text-violet-200/60'
            : 'bg-white/10 border-white/20 text-foreground placeholder:text-muted-foreground'
        }`}
      />
    ) : (
      <span className={`text-sm font-medium truncate ${
        isActive ? 'text-violet-50' : 'text-foreground'
      }`}>
        {conv.title || t('untitled')}
      </span>
    )}
    <div className="flex items-center gap-1 shrink-0">
      <button
        onClick={(e) => handleRenameClick(e, conv)}
        className={`p-1 rounded transition-colors ${
          isActive
            ? 'hover:bg-violet-400/20 text-violet-200 hover:text-white'
            : 'hover:bg-white/20 text-muted-foreground hover:text-foreground'
        }`}
        aria-label="Переименовать"
      >
        <Pencil size={12} />
      </button>
      <button
        onClick={(e) => handleDeleteClick(e, conv)}
        className={`p-1 rounded transition-colors ${
          isActive
            ? 'hover:bg-red-500/30 text-red-200 hover:text-white'
            : 'hover:bg-red-500/20 text-red-400 hover:text-red-300'
        }`}
        aria-label="Удалить"
      >
        <Trash2 size={12} />
      </button>
    </div>
  </div>
  <div className="flex items-center justify-between">
    <span className={`text-[10px] uppercase ${
      isActive ? 'text-violet-200/80' : 'text-muted-foreground'
    }`}>
      {conv.provider}
    </span>
    <span className={`text-[10px] ${
      isActive ? 'text-violet-200/80' : 'text-muted-foreground'
    }`}>
      {formatTimestamp(conv.lastUsedAt || conv.createdAt, t as (key: string) => string)}
    </span>
  </div>
</motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {deletingConversation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={cancelDelete}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm bg-white/10 dark:bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 shadow-xl"
            >
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t('delete')}?
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Удалить диалог «{deletingConversation.title || t('untitled')}»?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-foreground text-sm font-medium transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-xl bg-red-500/80 hover:bg-red-500 text-white text-sm font-medium transition-colors"
                >
                  {t('delete')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </aside>
  );
}